import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { api, AdminUserAccess, AdminUserReviewStatus } from '../../super-picker/services/api';

type ManageUsersTab = 'pending' | 'approved' | 'rejected_rejoined' | 'rejected_never';

interface Props {
  open: boolean;
  onClose: () => void;
}

function statusChip(status: AdminUserReviewStatus) {
  if (status === 'pending') return <Chip label="Pending" size="small" sx={{ bgcolor: '#D97706', color: '#fff', fontWeight: 700, fontSize: '0.7rem' }} />;
  if (status === 'approved') return <Chip label="Approved" size="small" color="success" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />;
  if (status === 'rejected') return <Chip label="Rejected" size="small" color="error" sx={{ fontWeight: 700, fontSize: '0.7rem' }} />;
  return <Chip label={status} size="small" sx={{ fontSize: '0.7rem' }} />;
}

function formatDate(val: string | null | undefined): string {
  if (!val) return '—';
  try {
    return new Date(val).toLocaleDateString('en-SG', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return val;
  }
}

export default function ManageUsersDialog({ open, onClose }: Props) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [users, setUsers] = useState<AdminUserAccess[]>([]);
  const [tab, setTab] = useState<ManageUsersTab>('pending');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [addCea, setAddCea] = useState('');
  const [addRole, setAddRole] = useState('AGENT');
  const [adding, setAdding] = useState(false);
  const [revokingCea, setRevokingCea] = useState<string | null>(null);
  const [loggingOutCea, setLoggingOutCea] = useState<string | null>(null);
  const [reviewingCea, setReviewingCea] = useState<string | null>(null);
  const [screenshotUser, setScreenshotUser] = useState<AdminUserAccess | null>(null);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getCalculatorAdminUsers();
      setUsers(data.users);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setTab('pending');
    setPage(0);
    fetchUsers();
  }, [open, fetchUsers]);

  const buckets = useMemo(() => {
    const pending: AdminUserAccess[] = [];
    const approved: AdminUserAccess[] = [];
    const rejectedRejoined: AdminUserAccess[] = [];
    const rejectedNever: AdminUserAccess[] = [];

    users.forEach((u) => {
      if (u.reviewStatus === 'pending') {
        pending.push(u);
      } else if (u.reviewStatus === 'approved') {
        approved.push(u);
      } else if (u.reviewStatus === 'rejected') {
        const hasRejoined = users.some(
          (other) => other.ceaNumber === u.ceaNumber && other.reviewStatus === 'pending',
        );
        if (hasRejoined) {
          rejectedRejoined.push(u);
        } else {
          rejectedNever.push(u);
        }
      }
    });

    return { pending, approved, rejectedRejoined, rejectedNever };
  }, [users]);

  const tabCounts = {
    pending: buckets.pending.length,
    approved: buckets.approved.length,
    rejected_rejoined: buckets.rejectedRejoined.length,
    rejected_never: buckets.rejectedNever.length,
  };

  const activeList = useMemo(() => {
    const raw =
      tab === 'pending' ? buckets.pending
      : tab === 'approved' ? buckets.approved
      : tab === 'rejected_rejoined' ? buckets.rejectedRejoined
      : buckets.rejectedNever;

    const q = query.trim().toLowerCase();
    const filtered = q
      ? raw.filter(
          (u) =>
            u.ceaNumber.toLowerCase().includes(q) ||
            (u.name || '').toLowerCase().includes(q) ||
            (u.email || '').toLowerCase().includes(q),
        )
      : raw;

    return {
      filtered,
      paginated: filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    };
  }, [tab, buckets, query, page, rowsPerPage]);

  const handleAdd = async () => {
    const cea = addCea.trim().toUpperCase();
    if (!cea) return;
    setAdding(true);
    setError(null);
    setSuccess(null);
    try {
      const data = await api.addCalculatorAdminUser(cea, addRole);
      setUsers((prev) => [data.user, ...prev]);
      setAddCea('');
      setSuccess(`User ${cea} added successfully`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add user');
    } finally {
      setAdding(false);
    }
  };

  const handleRevoke = async (ceaNumber: string) => {
    setRevokingCea(ceaNumber);
    setError(null);
    setSuccess(null);
    try {
      await api.revokeCalculatorAdminUser(ceaNumber);
      setUsers((prev) => prev.filter((u) => u.ceaNumber !== ceaNumber));
      setSuccess(`Access revoked for ${ceaNumber}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke user');
    } finally {
      setRevokingCea(null);
    }
  };

  const handleLogout = async (ceaNumber: string) => {
    setLoggingOutCea(ceaNumber);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.logoutCalculatorAdminUser(ceaNumber);
      setSuccess(`Logged out ${ceaNumber} (${result.sessionsRevoked} session${result.sessionsRevoked !== 1 ? 's' : ''} revoked)`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to logout user');
    } finally {
      setLoggingOutCea(null);
    }
  };

  const handleReview = async (ceaNumber: string, approved: boolean) => {
    setReviewingCea(ceaNumber);
    setError(null);
    setSuccess(null);
    try {
      const result = await api.reviewCalculatorAdminUser(ceaNumber, approved);
      setUsers((prev) =>
        prev.map((u) =>
          u.ceaNumber === ceaNumber
            ? {
                ...u,
                reviewedAt: result.user.reviewedAt,
                defunctAt: result.user.defunctAt,
                reviewStatus: result.user.reviewStatus,
              }
            : u,
        ),
      );
      if (!approved) {
        setTab('rejected_never');
        setPage(0);
      }
      setSuccess(approved ? `Access approved for ${ceaNumber}` : `Access rejected for ${ceaNumber}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to review user');
    } finally {
      setReviewingCea(null);
    }
  };

  const tabLabel = (label: string, count: number) =>
    count > 0 ? `${label} (${count})` : label;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth scroll="paper">
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            py: 1.5,
            borderBottom: '1px solid var(--color-border)',
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          }}
        >
          <Typography component="span" variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
            Manage Users
          </Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: 'var(--color-text-strong)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            bgcolor: 'var(--color-bg)',
            color: 'var(--color-text-strong)',
            p: { xs: 1.5, sm: 2 },
          }}
        >
          {error && (
            <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {/* Add user row */}
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <TextField
              size="small"
              label="CEA Number"
              value={addCea}
              onChange={(e) => setAddCea(e.target.value.toUpperCase())}
              sx={{ width: 180 }}
              inputProps={{ style: { textTransform: 'uppercase' } }}
            />
            <Box sx={{ minWidth: 110 }}>
              <InputLabel sx={{ fontSize: '0.75rem', mb: 0.25 }}>Role</InputLabel>
              <Select
                size="small"
                value={addRole}
                onChange={(e) => setAddRole(e.target.value)}
                sx={{ height: 40, minWidth: 110 }}
              >
                <MenuItem value="AGENT">AGENT</MenuItem>
                <MenuItem value="BUYING-CALC">BUYING-CALC</MenuItem>
                <MenuItem value="SELLING-CALC">SELLING-CALC</MenuItem>
                <MenuItem value="DECOUPLE-CALC">DECOUPLE-CALC</MenuItem>
                <MenuItem value="ADMIN">ADMIN</MenuItem>
              </Select>
            </Box>
            <Button
              variant="contained"
              onClick={handleAdd}
              disabled={adding || !addCea.trim()}
              sx={{ height: 40, textTransform: 'uppercase', fontWeight: 700 }}
            >
              {adding ? <CircularProgress size={18} color="inherit" /> : 'Add User'}
            </Button>
          </Box>

          {/* Search */}
          <TextField
            size="small"
            fullWidth
            placeholder="Search users..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setPage(0); }}
            sx={{ mb: 1.5 }}
          />

          {/* Tabs */}
          <Tabs
            value={tab}
            onChange={(_e, v) => { setTab(v as ManageUsersTab); setPage(0); }}
            sx={{ mb: 1, borderBottom: '1px solid var(--color-border)' }}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: '#D97706' } }}
          >
            <Tab value="pending" label={tabLabel('PENDING', tabCounts.pending)} sx={{ fontSize: '0.78rem', fontWeight: 600, color: tab === 'pending' ? '#D97706' : 'var(--color-text-subtle)' }} />
            <Tab value="approved" label={tabLabel('APPROVED', tabCounts.approved)} sx={{ fontSize: '0.78rem', fontWeight: 600, color: tab === 'approved' ? '#D97706' : 'var(--color-text-subtle)' }} />
            <Tab value="rejected_rejoined" label={tabLabel('REJECTED - REJOINED', tabCounts.rejected_rejoined)} sx={{ fontSize: '0.78rem', fontWeight: 600, color: tab === 'rejected_rejoined' ? '#D97706' : 'var(--color-text-subtle)' }} />
            <Tab value="rejected_never" label={tabLabel('REJECTED - NEVER BACK', tabCounts.rejected_never)} sx={{ fontSize: '0.78rem', fontWeight: 600, color: tab === 'rejected_never' ? '#D97706' : 'var(--color-text-subtle)' }} />
          </Tabs>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : activeList.filtered.length === 0 ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography sx={{ color: 'var(--color-text-faint)' }}>No users found.</Typography>
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ bgcolor: 'var(--color-surface)', borderRadius: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }}>CEA Number</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }}>Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }}>Review</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }}>Created</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }}>Screenshot</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: 'var(--color-text-subtle)', fontSize: '0.78rem' }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {activeList.paginated.map((user) => (
                    <TableRow key={user.id} hover sx={{ '& td': { color: 'var(--color-text-strong)', fontSize: '0.82rem', py: 0.75 } }}>
                      <TableCell>{user.ceaNumber}</TableCell>
                      <TableCell>{user.name || '—'}</TableCell>
                      <TableCell>{user.role}</TableCell>
                      <TableCell>{statusChip(user.reviewStatus)}</TableCell>
                      <TableCell>{formatDate(user.createdAt)}</TableCell>
                      <TableCell>
                        {user.imgUrl ? (
                          <Button
                            size="small"
                            variant="outlined"
                            endIcon={<OpenInNewIcon sx={{ fontSize: 14 }} />}
                            onClick={() => setScreenshotUser(user)}
                            sx={{ fontSize: '0.72rem', py: 0.25, px: 1, textTransform: 'uppercase' }}
                          >
                            Screenshot
                          </Button>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                          {user.reviewStatus === 'pending' && (
                            <>
                              <Tooltip title="Approve access">
                                <span>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    color="success"
                                    onClick={() => handleReview(user.ceaNumber, true)}
                                    disabled={reviewingCea === user.ceaNumber}
                                    sx={{ fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 0 }}
                                  >
                                    {reviewingCea === user.ceaNumber ? <CircularProgress size={12} color="inherit" /> : 'Approve'}
                                  </Button>
                                </span>
                              </Tooltip>
                              <Tooltip title="Reject access">
                                <span>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    onClick={() => handleReview(user.ceaNumber, false)}
                                    disabled={reviewingCea === user.ceaNumber}
                                    sx={{ fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 0 }}
                                  >
                                    Reject
                                  </Button>
                                </span>
                              </Tooltip>
                            </>
                          )}
                          {user.reviewStatus === 'approved' && (
                            <Tooltip title="Force logout (revoke sessions only)">
                              <span>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => handleLogout(user.ceaNumber)}
                                  disabled={loggingOutCea === user.ceaNumber}
                                  sx={{ fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 0 }}
                                >
                                  {loggingOutCea === user.ceaNumber ? <CircularProgress size={12} color="inherit" /> : 'Logout'}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          <Tooltip title="Revoke access permanently">
                            <span>
                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => handleRevoke(user.ceaNumber)}
                                disabled={revokingCea === user.ceaNumber}
                                sx={{ fontSize: '0.72rem', py: 0.25, px: 1, minWidth: 0 }}
                              >
                                {revokingCea === user.ceaNumber ? <CircularProgress size={12} color="inherit" /> : 'Revoke'}
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={activeList.filtered.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(_e, newPage) => setPage(newPage)}
                rowsPerPageOptions={[10, 25, 50]}
                onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                sx={{ color: 'var(--color-text-subtle)', '.MuiTablePagination-select': { color: 'var(--color-text-strong)' } }}
              />
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid var(--color-border)', px: 2, py: 1 }}>
          <Button onClick={onClose} sx={{ color: 'var(--color-text-subtle)' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Screenshot preview dialog */}
      <Dialog
        open={Boolean(screenshotUser)}
        onClose={() => setScreenshotUser(null)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            bgcolor: 'var(--color-surface)',
            color: 'var(--color-text-strong)',
          }}
        >
          Screenshot — {screenshotUser?.ceaNumber} {screenshotUser?.name ? `(${screenshotUser.name})` : ''}
          <IconButton onClick={() => setScreenshotUser(null)} size="small" sx={{ color: 'var(--color-text-strong)' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ bgcolor: 'var(--color-bg)', textAlign: 'center', py: 2 }}>
          {screenshotUser?.imgUrl && (
            <Box
              component="img"
              src={screenshotUser.imgUrl}
              alt="Access proof screenshot"
              sx={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 1 }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ bgcolor: 'var(--color-surface)', borderTop: '1px solid var(--color-border)' }}>
          <Button onClick={() => setScreenshotUser(null)} sx={{ color: 'var(--color-text-subtle)' }}>Close</Button>
          {screenshotUser?.reviewStatus === 'pending' && (
            <>
              <Button
                variant="contained"
                color="success"
                onClick={() => { handleReview(screenshotUser.ceaNumber, true); setScreenshotUser(null); }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() => { handleReview(screenshotUser.ceaNumber, false); setScreenshotUser(null); }}
              >
                Reject
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
